import * as React from 'react';
import {
	createBrowserRouter,
	useRouteError,
	useNavigate,
	useLocation,
	Outlet,
} from 'react-router-dom';
// import { SignIn, SignUp, useUser } from '@clerk/clerk-react'; // Removed Clerk imports
import App from './App';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { EngagementPodsAdmin } from './components/admin/EngagementPodsAdmin';
import { UrlManagement } from './components/admin/UrlManagement';
import { UsersManagement } from './components/admin/UsersManagement';
import { SocialProfiles } from './components/admin/SocialProfiles';
import { ApiKeyManagement } from './components/admin/ApiKeyManagement';
import { UrlHistory } from './components/admin/UrlHistory';
import { ProfileGroups } from './components/admin/ProfileGroups';
import { PodHistory } from './components/admin/PodHistory';

import { AIManagement } from './components/admin/AIManagement';
import { BrandVoice } from './components/admin/BrandVoice';
import { ContentMachine } from './components/admin/ContentMachine';
import ScheduleManagement from './components/admin/ScheduleManagement';
import { PageLoader } from '@/components/ui/loader';
import { ContentMachineAdmin } from './components/admin/ContentMachineAdmin';
import { BuzzAdmin } from './components/admin/BuzzAdmin';
import { Home } from './components/Home';
import { ContentHistory } from './components/admin/ContentHistory';
import { Archive } from './components/admin/Archive';
import { AdminProtectedRoute } from './components/admin/AdminProtectedRoute';
import { Oracle } from './components/shared/Oracle';
import { NotFound } from './components/NotFound';
import { AdminLayout } from './components/admin/AdminLayout';
import { GenUI } from './components/admin/GenUI';
import { NavigationListener } from './components/NavigationListener';
import { VideoContent } from './components/admin/VideoContent';
import { UserProfile } from './components/admin/UserProfile';
import { DebugPage } from './components/DebugPage';
import { VideoTimelinePage } from './components/admin/video/VideoTimelinePage';
import { AccessRequest } from './components/AccessRequest';
import { AnalyticsDashboard } from './components/admin/AnalyticsDashboard';
import { AnalyticsIndex } from './components/admin/analytics/AnalyticsIndex';
import { BuzzAgentGameAnalytics } from './components/admin/analytics/BuzzAgentGameAnalytics';
import { KarmaSocialRewardsAnalytics } from './components/admin/analytics/KarmaSocialRewardsAnalytics';
import { AffiliateRewardsAnalytics } from './components/admin/analytics/AffiliateRewardsAnalytics';
import { SocialProfileGameAnalytics } from './components/admin/analytics/SocialProfileGameAnalytics';
import { GameLifecycleControl } from './components/admin/analytics/GameLifecycleControl';
import { useWeb3Auth } from './providers/Web3AuthProvider';

function ErrorBoundary() {
	const error = useRouteError();
	const location = useLocation();

	console.log('Route error details:', {
		error,
		pathname: location.pathname,
		type: error instanceof Error ? 'Error' : typeof error,
	});

	// Only show NotFound for 404 errors
	if (error instanceof Error) {
		return (
			<div className='flex flex-col items-center justify-center min-h-screen p-4'>
				<h1 className='text-2xl font-bold text-pink-500 mb-4'>
					Oops! Something went wrong
				</h1>
				<p className='text-gray-300 mb-4'>{error.message}</p>
				<pre className='bg-gray-800 p-4 rounded-lg overflow-auto max-w-full'>
					{error.stack}
				</pre>
			</div>
		);
	}

	// For navigation/404 errors
	return <NotFound />;
}

function MobileWarningDialog() {
	return (
		<div className='fixed inset-0 z-[150] bg-black/90 backdrop-blur-md flex items-center justify-center p-4'>
			<div className='bg-gradient-to-b from-pink-500/10 to-purple-600/10 rounded-lg p-8 max-w-md mx-auto text-center border border-pink-500/20 shadow-2xl'>
				<div className='mb-6'>
					<span className='text-6xl animate-bounce inline-block'>
						🔥
					</span>
				</div>
				<h2 className='text-3xl font-bold text-pink-500 mb-4'>
					Oops! Too Much Power to Handle!
				</h2>
				<p className='text-gray-300 mb-6'>
					Prometheus needs a bigger screen to contain all its divine
					power! <br /> <br />
					For now, our fire-stealing capabilities are desktop only.
				</p>
				<p className='text-sm text-pink-400/80 italic'>
					"Even Zeus uses a desktop computer!"
					<br /> - Ancient Greek proverb (probably)
				</p>
			</div>
		</div>
	);
}

function RootWrapper() {
	const [isMobileWidth, setIsMobileWidth] = React.useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const isHomePage = location.pathname === '/';
	const isAdminRoute = location.pathname.startsWith('/admin');
	// Get auth from Web3Auth directly for early checking
	const { isLoggedIn, isWeb3AuthLoading, authAttemptCompleted } =
		useWeb3Auth();

	// Early login check to prevent showing spinner on protected routes
	// But only after initial load is complete
	React.useEffect(() => {
		// Skip this check on the home page
		if (isHomePage) return;

		// If trying to access admin route:
		// - Wait for auth attempt to complete.
		// - If auth attempt completed AND user is not logged in, redirect to home.
		// - isWeb3AuthLoading check ensures we don't redirect while auth is actively processing.
		if (
			isAdminRoute &&
			authAttemptCompleted &&
			!isLoggedIn &&
			!isWeb3AuthLoading
		) {
			console.log(
				'Routes: Auth completed, not logged in. Redirecting from admin route to home',
				{
					path: location.pathname,
					isLoggedIn,
					authAttemptCompleted,
					isWeb3AuthLoading,
				}
			);
			navigate('/', { replace: true });
		}
	}, [
		isAdminRoute,
		isLoggedIn,
		isWeb3AuthLoading,
		authAttemptCompleted,
		navigate,
		isHomePage,
		location.pathname,
	]);

	React.useEffect(() => {
		const checkWidth = () => {
			setIsMobileWidth(window.innerWidth < 1200);
		};

		checkWidth();
		window.addEventListener('resize', checkWidth);
		return () => window.removeEventListener('resize', checkWidth);
	}, []);

	// React.useEffect(() => {
	// 	console.log('Current route:', {
	// 		pathname: location.pathname,
	// 		isHomePage,
	// 		isMobileWidth,
	// 		isLoggedIn,
	// 		initialLoadComplete,
	// 	});
	// }, [location, isHomePage, isMobileWidth, isLoggedIn, initialLoadComplete]);

	// Error handling useEffect remains the same
	React.useEffect(() => {
		const handleError = (error: Error) => {
			console.error('Route error:', error);
		};

		window.addEventListener('error', (e) => handleError(e.error));
		return () =>
			window.removeEventListener('error', (e) => handleError(e.error));
	}, []);

	return (
		<React.Suspense fallback={<PageLoader message='Loading...' />}>
			{/* Mobile warning check no longer needs isAuthPage */}
			{isMobileWidth && !isHomePage && <MobileWarningDialog />}
			<NavigationListener />
			<Outlet />
		</React.Suspense>
	);
}

export const router = createBrowserRouter([
	{
		path: '/',
		element: <RootWrapper />,
		errorElement: <ErrorBoundary />,
		children: [
			{
				element: <App />,
				children: [
					{
						path: 'auth/callback',
						element: <PageLoader message='Signing in...' />,
					},
					{
						index: true,
						element: <Home />,
					},
					{
						path: 'access-request',
						element: <AccessRequest />,
					},
					{
						path: 'debug',
						element: <DebugPage />,
					},
					{
						path: 'admin',
						element: <AdminProtectedRoute />,
						children: [
							{
								element: <AdminLayout />,
								children: [
									{
										index: true,
										element: <AdminDashboard />,
									},
									{
										path: 'profile',
										element: <UserProfile />,
									},
									{
										path: 'pods',
										element: <EngagementPodsAdmin />,
									},
									{
										path: 'pods/history',
										element: <PodHistory />,
									},
									{
										path: 'urls',
										children: [
											{
												path: '',
												element: <UrlManagement />,
											},
											{
												path: 'history',
												element: <UrlHistory />,
											},
										],
									},
									{
										path: 'users',
										element: <UsersManagement />,
									},
									{
										path: 'profiles',
										element: <SocialProfiles />,
									},
									{
										path: 'keys',
										element: <ApiKeyManagement />,
									},
									{
										path: 'groups',
										element: <ProfileGroups />,
									},
									{
										path: 'ai',
										element: <AIManagement />,
									},
									{
										path: 'voice',
										element: <BrandVoice />,
									},
									{
										path: 'schedules',
										element: <ScheduleManagement />,
									},
									{
										path: 'buzz',
										element: <BuzzAdmin />,
									},
									{
										path: 'content-machine',
										children: [
											{
												path: '',
												element: <ContentMachine />,
											},
											{
												path: ':id',
												element: (
													<ContentMachineAdmin />
												),
											},
											{
												path: ':id/history',
												element: <ContentHistory />,
											},
										],
									},
									{
										path: 'video',
										children: [
											{
												index: true,
												element: <VideoContent />,
											},
											{
												path: 'timeline/:videoId',
												element: <VideoTimelinePage />,
											},
										],
									},
									{
										path: 'oracle',
										element: <Oracle />,
									},
									{
										path: 'analytics',
										children: [
											{
												index: true,
												element: <AnalyticsIndex />,
											},
											{
												path: 'karma-social',
												element: <KarmaSocialRewardsAnalytics />,
											},
											{
												path: 'affiliate',
												element: <AffiliateRewardsAnalytics />,
											},
											{
												path: 'social-game',
												element: <SocialProfileGameAnalytics />,
											},
											{
												path: 'buzz-agents',
												element: <BuzzAgentGameAnalytics />,
											},
											{
												path: 'lifecycle',
												element: <GameLifecycleControl />,
											},
										],
									},
									{
										path: 'content',
										element: <Archive />,
									},
									{
										path: 'genui',
										element: <GenUI />,
									},
								],
							},
						],
					},
					{
						path: 'user',
						element: <AdminProtectedRoute />, // Users still need to be authenticated
						children: [
							{
								element: <AdminLayout />, // Reuse the same layout
								children: [
									{
										index: true,
										element: <AdminDashboard />, // For now, same dashboard
									},
									{
										path: 'profile',
										element: <UserProfile />,
									},
									{
										path: 'urls',
										element: <UrlManagement />,
									},
									{
										path: 'oracle',
										element: <Oracle />,
									},
									{
										path: 'analytics',
										children: [
											{
												index: true,
												element: <AnalyticsIndex />,
											},
											{
												path: 'karma-social',
												element: <KarmaSocialRewardsAnalytics />,
											},
											{
												path: 'affiliate',
												element: <AffiliateRewardsAnalytics />,
											},
											{
												path: 'social-game',
												element: <SocialProfileGameAnalytics />,
											},
											{
												path: 'buzz-agents',
												element: <BuzzAgentGameAnalytics />,
											},
											{
												path: 'lifecycle',
												element: <GameLifecycleControl />,
											},
										],
									},
								],
							},
						],
					},
					// Removed /sign-in and /sign-up routes
					// Web3Auth handles sign-in/up via its modal triggered by the `login` function
				],
			},
		],
	},
]);
